import type { ReactElement } from 'react';
import { SquareArrowOutUpRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';

const POPUP_FEATURES = 'popup,width=1440,height=900';

/**
 * Brings up the alert's own window: an existing one is focused as it is (its
 * unsaved drafts and open dialogs survive); otherwise a new one loads `path`.
 */
function openAlertWindow(path: string, name: string): void {
  // An empty URL finds a window by name without navigating it, or opens a
  // blank one when there is none.
  const win = window.open('', name, POPUP_FEATURES);
  if (!win) return;
  let isBlank = true;
  try {
    isBlank = win.location.href === 'about:blank';
  } catch {
    // The window navigated to another origin; take it back for this alert.
  }
  if (isBlank) win.location.replace(path);
  win.focus();
}

interface AlertPopOutButtonProps {
  alertKey: string;
  /** Omitted → the personal-dashboard (account) alert. */
  organizationId?: string;
}

/**
 * Opens the alert's detail in its own browser window (`AlertWindowPage`).
 * Named per alert and per organization (or personal account), so pressing it
 * again for the same alert brings that window forward instead of opening or
 * reloading one.
 */
export function AlertPopOutButton({
  alertKey,
  organizationId,
}: AlertPopOutButtonProps): ReactElement {
  const path = `${organizationId ? `/${organizationId}` : '/dashboard'}/alerts/${encodeURIComponent(alertKey)}/window`;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          variant="outline"
          size="icon-sm"
          aria-label="Open alert in a new window"
          data-testid="alert-pop-out-button"
          onClick={event => {
            // Never toggle the row underneath.
            event.stopPropagation();
            openAlertWindow(
              path,
              `kirin-alert-${organizationId ?? 'personal'}-${alertKey}`
            );
          }}
        >
          <SquareArrowOutUpRight className="size-4" aria-hidden />
        </Button>
      </TooltipTrigger>
      <TooltipContent>Open in a new window</TooltipContent>
    </Tooltip>
  );
}

AlertPopOutButton.displayName = 'AlertPopOutButton';
